import React, { useEffect, useMemo, useState } from "react";
import { fetchTeams, createChallenge } from "../../lib/api";
import { SmartScheduleSuggestions } from "../SmartScheduleSuggestions";

/**
 * ScheduleMatchModal — fewer-clicks match invite.
 *
 * Flow: pick my (managed) team → opponent (auto-filtered to same league) →
 * date+time → submit. Hits the existing /api/team-challenges endpoint via
 * createChallenge.
 */
export function ScheduleMatchModal({ myTeams, onClose, onToast, onCreated }) {
  const [fromTeamId, setFromTeamId] = useState(myTeams[0]?.team_id || "");
  const [opponents, setOpponents] = useState([]);
  const [toTeamId, setToTeamId] = useState("");
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [time, setTime] = useState("20:00");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const fromTeam = useMemo(
    () => myTeams.find((t) => t.team_id === fromTeamId),
    [fromTeamId, myTeams]
  );

  useEffect(() => {
    setToTeamId("");
    if (!fromTeam) { setOpponents([]); return; }
    fetchTeams(fromTeam.league_id)
      .then((all) => setOpponents(all.filter((t) => t.team_id !== fromTeam.team_id)))
      .catch(() => setOpponents([]));
  }, [fromTeam]);

  const submit = async () => {
    if (!fromTeamId || !toTeamId || !date || !time) {
      onToast?.("Pick both teams, a date and a time.");
      return;
    }
    setBusy(true);
    try {
      await createChallenge({
        from_team_id: fromTeamId,
        to_team_id: toTeamId,
        scheduled_at: new Date(`${date}T${time}:00`).toISOString(),
        note: note.trim(),
      });
      onToast?.("⚡ Challenge sent — waiting on their inbox.");
      onCreated?.();
      onClose();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't send challenge.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
         onClick={onClose} data-testid="schedule-match-backdrop">
      <div className="glass neon-glow-pink rounded-3xl p-6 w-full max-w-lg"
           onClick={(e) => e.stopPropagation()}
           data-testid="schedule-match-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-calendar-days mr-1" />SCHEDULE A MATCH
            </p>
            <h2 className="font-display text-2xl grad-text">Invite a team</h2>
          </div>
          <button onClick={onClose} data-testid="schedule-match-close"
                  className="text-white/60 hover:text-white text-2xl">
            <i className="fas fa-times" />
          </button>
        </div>

        {myTeams.length === 0 ? (
          <p className="text-sm text-white/55 py-6 text-center" data-testid="schedule-no-teams">
            You don't manage any teams yet. Create or own a team in a league first.
          </p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">YOUR TEAM</label>
              <select value={fromTeamId} onChange={(e) => setFromTeamId(e.target.value)}
                      data-testid="schedule-from-team"
                      className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm">
                {myTeams.map((t) => (
                  <option key={t.team_id} value={t.team_id}>
                    {t.league_name ? `${t.name} · ${t.league_name}` : t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">OPPONENT</label>
              {opponents.length === 0 ? (
                <p className="text-[12px] text-white/45 py-2">No other teams in this league yet.</p>
              ) : (
                <select value={toTeamId} onChange={(e) => setToTeamId(e.target.value)}
                        data-testid="schedule-to-team"
                        className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm">
                  <option value="">— Pick an opponent —</option>
                  {opponents.map((t) => (
                    <option key={t.team_id} value={t.team_id}>
                      {t.name} · MMR {t.mmr || 1500}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">DATE</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                       data-testid="schedule-date"
                       className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">TIME</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                       data-testid="schedule-time"
                       className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">NOTE (OPTIONAL)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="BO3? GG?"
                     data-testid="schedule-note"
                     className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30" />
            </div>

            {/* Wave C — Smart Schedule suggestions inside the dashboard flow */}
            {fromTeamId && toTeamId && (
              <SmartScheduleSuggestions
                fromTeamId={fromTeamId}
                toTeamId={toTeamId}
                onToast={onToast}
                onPick={(iso) => {
                  try {
                    const d = new Date(iso);
                    const pad = (n) => String(n).padStart(2, "0");
                    setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
                    setTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
                    onToast?.("Time picked from suggestions — review and send.");
                  } catch { /* ignore */ }
                }}
              />
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose}
                      data-testid="schedule-cancel"
                      className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
                CANCEL
              </button>
              <button type="button" onClick={submit} disabled={busy || !toTeamId}
                      data-testid="schedule-submit"
                      className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-60">
                {busy ? "..." : "SEND CHALLENGE"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
